import React, { useEffect, useState, useCallback } from 'react';
import './BrochureViewer.css';

/**
 * BrochureViewer
 * Uses Google Docs PDF viewer as primary renderer (works everywhere)
 * Falls back to direct open / download if needed.
 *
 * Props:
 *   pdfUrl  – e.g. "/brochure/KV.pdf"
 *   title   – project name
 *   onClose – close handler
 */
export default function BrochureViewer({ pdfUrl, title, onClose }) {
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('embed'); // 'embed' | 'google' | 'fallback'

  /* lock body scroll */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  /* ESC to close */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  /* Absolute URL of the PDF */
  const absUrl = `${window.location.origin}${pdfUrl}`;

  /* Google Docs viewer URL — works even when Chrome blocks direct embed */
  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(absUrl)}&embedded=true`;

  /* Direct download using fetch → Blob (forces browser Save dialog) */
  const handleDownload = useCallback(async () => {
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${title.replace(/\s+/g, '_')}_Brochure.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch {
      /* fallback: direct link */
      const a = document.createElement('a');
      a.href = absUrl;
      a.download = `${title.replace(/\s+/g, '_')}_Brochure.pdf`;
      a.click();
    }
  }, [pdfUrl, absUrl, title]);

  /* Open PDF in new browser tab */
  const handleOpenTab = () => window.open(absUrl, '_blank', 'noopener,noreferrer');

  /* Switch to Google viewer */
  const handleGoogleView = () => { setViewMode('google'); setLoading(true); };

  /* Current iframe src */
  const iframeSrc = viewMode === 'google' ? googleViewerUrl : absUrl;

  return (
    <div className="bv-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bv-modal">

        {/* ── HEADER ── */}
        <div className="bv-header">
          <div className="bv-title">
            <i className="fa fa-file-pdf" />
            <span>{title} — Official Brochure</span>
          </div>
          <div className="bv-header-actions">
            {viewMode !== 'google' && (
              <button className="bv-alt-btn" onClick={handleGoogleView} title="Use Google Docs viewer">
                <i className="fa fa-globe" /> Use Google Viewer
              </button>
            )}
            <button className="bv-open-btn" onClick={handleOpenTab} title="Open in new tab">
              <i className="fa fa-external-link-alt" /> Open Tab
            </button>
            <button className="bv-download-btn" onClick={handleDownload}>
              <i className="fa fa-download" /> Download PDF
            </button>
            <button className="bv-close" onClick={onClose} aria-label="Close">
              <i className="fa fa-times" />
            </button>
          </div>
        </div>

        {/* ── MODE TABS ── */}
        <div className="bv-mode-bar">
          <button
            className={`bv-mode-btn ${viewMode === 'embed' ? 'active' : ''}`}
            onClick={() => { setViewMode('embed'); setLoading(true); }}
          >
            <i className="fa fa-desktop" /> Inline View
          </button>
          <button
            className={`bv-mode-btn ${viewMode === 'google' ? 'active' : ''}`}
            onClick={handleGoogleView}
          >
            <i className="fa fa-globe" /> Google Docs Viewer
          </button>
          <span className="bv-mode-tip">
            If PDF is blank below, click "Google Docs Viewer" or "Open Tab"
          </span>
        </div>

        {/* ── PDF FRAME ── */}
        <div className="bv-frame-wrap">
          {loading && (
            <div className="bv-loading">
              <div className="bv-spinner" />
              <p>Loading PDF…</p>
            </div>
          )}
          <iframe
            key={iframeSrc}
            src={iframeSrc}
            title={`${title} Brochure`}
            className="bv-iframe"
            style={{ opacity: loading ? 0 : 1 }}
            onLoad={() => setLoading(false)}
            allow="fullscreen"
          />
        </div>

        {/* ── FOOTER ── */}
        <div className="bv-footer">
          <span className="bv-note">
            <i className="fa fa-info-circle" /> Press ESC to close · Scroll inside to browse all pages
          </span>
          <div className="bv-footer-btns">
            <button className="bv-download-btn-sm" onClick={handleOpenTab}>
              <i className="fa fa-external-link-alt" /> Open in Tab
            </button>
            <button className="bv-download-btn-sm red" onClick={handleDownload}>
              <i className="fa fa-download" /> Download
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
